import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
} from 'lucide-react'
import { printBookApi, type PrintBookListItem } from '@/api/printBook'

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: '초안', color: 'bg-slate-100 text-slate-600' },
  ready: { label: '준비됨', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: '완료', color: 'bg-green-100 text-green-700' },
}

export function BookListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteTarget, setDeleteTarget] = useState<PrintBookListItem | null>(null)

  const { data: books, isLoading, error } = useQuery({
    queryKey: ['print-books'],
    queryFn: printBookApi.getAll,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => printBookApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-books'] })
      setDeleteTarget(null)
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-red-700 bg-red-50 rounded-lg">
        <AlertCircle className="w-5 h-5" />
        <span>책 목록을 불러오는데 실패했습니다.</span>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">인쇄용 책 관리</h1>
            <p className="text-sm text-slate-500">커리큘럼 기반 인쇄용 워크북을 관리합니다</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/contents/books/new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
        >
          <Plus className="w-4 h-4" />
          새 책 만들기
        </button>
      </div>

      {/* Table */}
      {books && books.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">제목</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">라벨</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">모듈</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">레슨</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">페이지</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">컨펌</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">상태</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">액션</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {books.map((book) => {
                const statusCfg = STATUS_CONFIG[book.status] || STATUS_CONFIG.draft
                const allConfirmed = book.totalLessonCount > 0 && book.confirmedLessonCount === book.totalLessonCount
                return (
                  <tr
                    key={book.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/contents/books/${book.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 text-sm">{book.title}</div>
                      {book.subtitle && (
                        <div className="text-xs text-slate-400">{book.subtitle}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{book.bookLabel}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-center">{book.moduleCount}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-center">{book.lessonCount}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-center">
                      {book.totalPageCount ? `${book.totalPageCount}p` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs ${allConfirmed ? 'text-green-600' : 'text-slate-400'}`}>
                        {allConfirmed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                        {book.confirmedLessonCount}/{book.totalLessonCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(book)
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="삭제"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">아직 생성된 책이 없습니다</p>
          <button
            onClick={() => navigate('/contents/books/new')}
            className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            첫 번째 책 만들기
          </button>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteTarget(null)}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">책 삭제</h3>
            <p className="text-sm text-slate-600 mb-6">
              <strong>"{deleteTarget.title}"</strong>을(를) 삭제하시겠습니까?<br />
              이 작업은 되돌릴 수 없습니다.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
